import { useEffect, useRef, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Box } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { getColumnHeadersBasic } from './getStationsPageTableColumnHeaders.js' 
import { fetchChannelDataRows } from '../../utilities/apiRequests.js';
import { useHandleResizeError } from '../../utilities/handleResizeError.js';
import { getPaginationStyle } from './StationsPageTableSubComponentStyling.js';
import { useNavigate } from 'react-router';
import { getPlatform, useScreenOrientation } from "../../utilities/orientationAndPlatform.js";
import { useTheme } from '@mui/material/styles';
import { SortingButtons } from './SortingButtons.js';


export default function StationsPageTableBasic() {
  const theme = useTheme();
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [currentlySortedBy, setCurrentlySortedBy] = useState("");
  const [originalRows, setOriginalRows] = useState([]);
  const [callSignRows, setCallSignRows] = useState([]);
  const [affiliationRows, setAffiliationRows] = useState([]);
  const [marketRows, setMarketRows] = useState([]);

  const columnHeaders = useRef(getColumnHeadersBasic());

  let boxStyling = {
    backgroundColor: 'TableCell.background',
    border: theme.palette.TableCell.border,
    hoverColor: 'TableCell.hover',
  }

  // fetch rows from api
  const [shownRows, setShownRows] = useState([]);
  useEffect(() => {
    getAccessTokenSilently({ authorizationParams: { scope: "read:channels" } })
      .then((accessToken) => {
        // fetch data for columns specified in column headers
        const fields = columnHeaders.current.map((col) => col.field);
        return fetchChannelDataRows(accessToken, fields);
      })
      .then((fetchedRows) => {
        setShownRows(fetchedRows) 
        setOriginalRows(fetchedRows)
        setAffiliationRows(sortRowsFromData(fetchedRows, 'affiliation'));
        setCallSignRows(sortRowsFromData(fetchedRows, 'call_sign'));
        setMarketRows(sortRowsFromData(fetchedRows, 'market'));
      })
      .catch((error) => console.error("Error fetching rows:", error));
  }, [getAccessTokenSilently]);

  
  const sortRowsFromData = (rows, fieldToSortBy) => {
  return [...rows].sort((rowA, rowB) => {
    const valA = rowA[fieldToSortBy] || '';
    const valB = rowB[fieldToSortBy] || '';
    return valA.localeCompare(valB);
  });
};


  const handleSort = (fieldToSortBy) => {
    if (currentlySortedBy === fieldToSortBy) {
      setShownRows(originalRows);
      setCurrentlySortedBy("");
    }
    else {
      switch (fieldToSortBy) {
        case 'call_sign':
          setShownRows(callSignRows);
          setCurrentlySortedBy('call_sign');
          break;
        case 'affiliation':
          setShownRows(affiliationRows);
          setCurrentlySortedBy('affiliation');
          break;
        case 'market':
          setShownRows(marketRows);
          setCurrentlySortedBy('market');
          break;
        default:
          console.error("Unknown field to sort by:", fieldToSortBy);
      }
    }
  }


  useHandleResizeError()
  const paginationStyle = getPaginationStyle()

  const orientation = useScreenOrientation()
  const platform = getPlatform()
  let topPadding = "0.5em"
  let tableWidth = ""
  let headersLeftMargin = "0px"
  let headersRightMargin = "0px"
  if (platform === "mobile"){
    tableWidth = "100%"
    if (orientation === 0) {
      headersLeftMargin = '15%'
      headersRightMargin = '-7.5%'
    }
  } 
  else if (platform === "desktop"){
    tableWidth = "98%"
    topPadding = '1em'
  }

  return (
    <>    
      {/*Sort options */}
      <SortingButtons
        currentlySortedBy={currentlySortedBy}
        setCurrentlySortedBy={setCurrentlySortedBy}
        sortRows={handleSort}
        topPadding={topPadding} 
        theme={theme}
      />
      {/*MUI Table*/}
      <Box sx={{width: tableWidth }} id={"StationsPageTableBoxBasic"}>
        <DataGrid
          //data
          rows={shownRows}
          onRowClick={({ id }) => navigate(`/channelpage/${id}`)}
          getRowId={(row) => row.channel_id}
          columns={columnHeaders.current}
          disableColumnSorting={true}
          //Formatting
          getEstimatedRowHeight={() => 66}
          autoHeight //deprecated argument, but can't find any other way to mute console mui useResizeContainer warning. Might be an MUI bug.
          getRowHeight={() => "auto"} 
          sx={{
            backgroundColor: boxStyling.backgroundColor,
            border: 'none',
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            '& .MuiDataGrid-columnHeaderTitleContainerContent' : {
              overflow: 'visible',
              marginLeft: headersLeftMargin,
              marginRight: headersRightMargin,
            },
            "& .MuiDataGrid-scrollbar": {
              overflow: "hidden",
            },
            '& .MuiDataGrid-cell': {
              padding: '0px',
              borderBottom: boxStyling.border,
              borderLeft: theme.palette.TableCell.borderLeft,
              borderRight:'0px',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
            },
            '& .MuiDataGrid-cellEmpty': {
              borderLeft: '0px !important',
            },
            '& .MuiDataGrid-scrollbarFiller': {
              borderLeft: '0px !important',
              borderBottom: theme.palette.TableCell.borderLeft,
            },
            '& .MuiDataGrid-columnHeaders': {
              display: 'none' 
            },
            '& .MuiDataGrid-row:hover': {
              cursor: 'pointer', 
              backgroundColor: {boxStyling}.hoverColor,
            },
            '& .MuiDataGrid-footerContainer': {
              border: 'none',
            },
          }}      
          //footer styling for proper screen fitting
          slotProps={{
            pagination: {sx: paginationStyle},
          }}
          //disable unused options
          hideFooterSelectedRowCount={true}
          disableColumnResize={true}
          disableColumnMenu={true}
          //Pagination options
          initialState={{pagination: { paginationModel: { page: 0, pageSize: 15}}}}
          pageSizeOptions={[15, 25, 50, 100, { value: -1, label: 'All' }]}
        />
      </Box>
    </>
  );
}
